import Query from "./query";

export default class Model {
  constructor(props) {
    Object.assign(this, props);
  }

  /**
   * all - Retrieves all instances of the calling class
   * CAUTION: Do not use this for large data sets.
   */
  static all(pagination = false) {
    const query = new Query(this);
    return query.all(pagination);
  }

  /**
   * where - Sets the conditions for the query
   * @param {*} q - conditions ex: {name_eq: 'bla'}
   */
  static where(q) {
    const query = new Query(this);
    return query.where(q);
  }

  /**
   * find - Retrieves an instance of the calling class specified by id.
   * @param {number, string} id - The instance to retrieve
   */
  static find(id) {
    const query = this.perPage(1);
    return query.find(id);
  }

  /**
   * perPage - Sets the perPage for the query
   * @param {int} perPage - Number of results perPage
   */
  static perPage(perPage) {
    const query = new Query(this);
    return query.perPage(perPage);
  }

  /**
   * page - Sets the page for the query
   * @param {int} page - Results page based on perPage
   */
  static page(page) {
    const query = new Query(this);
    return query.page(page);
  }

  /**
   * sort - Sets the order for the query
   * @param {string} sort - Example: 'name asc'
   * @returns {instanceof Query} An instance of query
   */
  static sort(sort) {
    const query = new Query(this);
    return query.where({ s: sort });
  }

  /**
   * url - An optional method for overriding the class set static URL
   * Useful when retrieving nested resources. (ex: /api/v1/job_posts/:id/sub_categories)
   * @param {string} url - url to make the call to
   */
  static url(url) {
    const query = new Query(this);
    return query.url(url);
  }

  /**
   * params - An optional method for adding params to the query
   * @param {object} params - key value pairs to add to the query
   */
  static params(params) {
    const query = new Query(this);
    return query.params(params);
  }

  /**
   * fields - An optional method for adding field params to the query
   * @param {string} fields - string to add to the fields param
   */
  static fields(fields) {
    const query = new Query(this);
    return query.fields(fields);
  }

  /**
   * classUrl - Allows the class that extends Model to use a method instead of just
   * hard coding URL
   */
  static classUrl() {
    return this.URL;
  }

  /**
   * get - Wrapper for the get method on the query
   * @param {number} id - Optional: ID to look up
   */
  static get(id) {
    const query = new Query(this);
    return query.get(id);
  }

  static assign(props) {
    Object.assign(this, props);
    const query = new Query(this);
    return query.assign(props);
  }

  /**
   * loadAll - Loads every page for a query that returns paginated results via .all()
   * @param {function(number): Query} buildQuery - receives page number, must return a Query
   * @returns {Promise<Array>}
   */
  static async loadAll(buildQuery) {
    let page = 1;
    let totalPages = 1;
    let allResults = [];

    do {
      const response = await buildQuery(page).all();
      const results = response?.results || [];

      totalPages = response?.totalPages || response?.total_pages || 1;
      allResults = allResults.concat(results);
      page += 1;
    } while (page <= totalPages);

    return allResults;
  }

  /**
   * data - Strips out instance functions for saving
   */
  get data() {
    const data = {};

    Object.keys(this).forEach((key) => {
      if (key === "errorMessages") return;
      if (typeof this[key] === "function") return;

      data[key] = this[key];
    });

    return data;
  }

  /**
   * url - Allows for the setting of a URL on an instance.
   * @param {string} url
   * @returns {Model}
   */
  url(url) {
    this._url = url;
    return this;
  }

  /**
   * params - Adds params to the query for this instance.
   * @param {object} params
   * @returns {Model}
   */
  params(params) {
    this._params = params;
    return this;
  }

  /**
   * update - Sends specified attributes to the backend.
   * @param {Object} data
   * @returns {Promise}
   */
  update(data) {
    const query = new Query(this.constructor);

    if (this._url) query.url(this._url);
    if (this._params) query.params(this._params);

    return query.update(this.data, data);
  }

  /**
   * post - Sends all attributes as a POST even if id is present.
   * Useful for custom action endpoints that should not PATCH.
   * @returns {Promise}
   */
  post() {
    const query = new Query(this.constructor);

    if (this._url) query.url(this._url);
    if (this._params) query.params(this._params);

    return query.post(this.data);
  }

  /**
   * save - Posts or patches depending on if id is present.
   * @returns {Promise}
   */
  save() {
    const query = new Query(this.constructor);

    if (this._url) query.url(this._url);
    if (this._params) query.params(this._params);

    return query.save(this.data);
  }

  /**
   * patch - Gives direct access to query.patch.
   * @param {number|string} id
   * @returns {Promise}
   */
  patch(id) {
    const query = new Query(this.constructor);

    if (this._url) query.url(this._url);
    if (this._params) query.params(this._params);

    return query.patch(id, this.data);
  }

  /**
   * destroy - Deletes this from the backend.
   * @returns {Promise}
   */
  destroy() {
    const query = new Query(this.constructor);

    if (this._url) query.url(this._url);
    if (this._params) query.params(this._params);

    return query.destroy(this.data);
  }

  /**
   * delete - Direct access to query.delete.
   * @returns {Promise}
   */
  delete() {
    const query = new Query(this.constructor);

    if (this._url) query.url(this._url);
    if (this._params) query.params(this._params);

    return query.delete((this.data || {}).id);
  }

  /**
   * toString - Converts object to a string
   * @returns {String}
   */
  toString() {
    return JSON.stringify(this.data);
  }
}
